import { useState, useEffect, useCallback } from 'react';

import { PaymentSystem } from '../constants';
import { getPaymentSystem } from '../utils/storage';

import usePayment from './use-payment';

const useApplePay = (options = {}, callbacks = {}) => {
  const paymentSystem = getPaymentSystem();

  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(null);

  const payment = usePayment();

  const handleError = (error) => {
    console.log('[APPLE PAY ERROR]', error);
  };

  const handleAddress = useCallback(
    (e) => {
      if (callbacks?.onShippingAddress) {
        callbacks.onShippingAddress(e?.payment?.shippingContact);
      }
    },
    [callbacks?.onShippingAddress]
  );

  useEffect(() => {
    if (paymentSystem === PaymentSystem.SOLIDGATE) {
      if (window.ApplePaySession && window.ApplePaySession.canMakePayments) {
        try {
          setCanMakePayment(window.ApplePaySession.canMakePayments());
        } catch (ex) {
          console.warn(ex);
        }
      }
    }
  }, [paymentSystem]);

  useEffect(() => {
    if (payment && options.currency) {
      let request = null;
      let paymentOptions = {
        country: options.country || 'US',
        currency: options.currency,
      };

      if (paymentSystem === PaymentSystem.RECURLY) {
        paymentOptions = {
          ...paymentOptions,
          label: options.label,
          total: String(options.amount),
        };
        try {
          request = payment.ApplePay(paymentOptions);
          setPaymentRequest(request);
        }
        catch (ex) { }
      } else if (paymentSystem === PaymentSystem.STRIPE) {
        // A new request is built whenever the total changes (e.g. the special
        // offer swaps the intro price). Drop the previous request and its
        // canMakePayment result first: Stripe requires canMakePayment() on the
        // very request that gets mounted, and while the old result is still
        // truthy the button would mount the new request too early and keep
        // showing the old total in the Google Pay / Apple Pay sheet.
        setPaymentRequest(null);
        setCanMakePayment(null);

        // Stripe expects:
        //   - amount as an integer in the currency's smallest unit
        //     (e.g. cents for USD, so 24.99 → 2499)
        //   - currency as a lowercase ISO code (e.g. "usd", not "USD")
        const toCents = (v) => Math.round(Number(v) * 100);

        paymentOptions = {
          ...paymentOptions,
          currency: String(paymentOptions.currency || '').toLowerCase(),
          total: {
            label: options.label,
            amount: toCents(options.amount),
          },
          // Deferred-email flow: ask Apple Pay for the payer's email (pre-filled
          // from Apple Pay settings) so we can skip the email-after-payment page.
          ...(options.requestPayerEmail ? { requestPayerEmail: true } : {}),
          // Billing-address flow: requesting the payer's name is what makes
          // Stripe return the billing address in `billing_details`. Phone and
          // shipping are not requested — only email + billing address.
          ...(options.requestPayerName ? { requestPayerName: true } : {}),
        };

        if (Array.isArray(options.displayItems) && options.displayItems.length) {
          paymentOptions.displayItems = options.displayItems.map((item) => ({
            label: item.label,
            amount: toCents(item.amount),
          }));
        }

        try {
          request = payment.paymentRequest(paymentOptions);
          setPaymentRequest(request);
        } catch (ex) {
          console.warn('[APPLE PAY] paymentRequest init failed', ex);
        }
      } else if (paymentSystem === PaymentSystem.SOLIDGATE) {
        paymentOptions = {
          ...paymentOptions,
          label: options.label,
          amount: String(options.amount),
        };
        try {
          request = payment.ApplePay(paymentOptions);
          setPaymentRequest(request);
        }
        catch (ex) {
          console.warn(ex);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSystem, options]);

  useEffect(() => {
    let subscribed = true;
    if (paymentRequest) {
      if (paymentSystem === PaymentSystem.RECURLY) {
        paymentRequest.ready(() => {
          if (subscribed) {
            setCanMakePayment(true);
          }
        });
      } else if (paymentSystem === PaymentSystem.STRIPE) {
        paymentRequest.canMakePayment().then((res) => {
          if (res && subscribed) {
            setCanMakePayment(res);
          }
        });
      } else if (paymentSystem === PaymentSystem.SOLIDGATE) {
        paymentRequest.canMakePayment().then((res) => {
          if (res && subscribed) {
            setCanMakePayment(res);
          }
        });
      }
    }

    return () => {
      subscribed = false;
    };
  }, [paymentRequest, paymentSystem]);

  useEffect(() => {
    if (paymentRequest) {
      const event =
        paymentSystem === PaymentSystem.RECURLY
          ? 'token'
          : paymentSystem === PaymentSystem.STRIPE
            ? 'paymentmethod'
            : paymentSystem === PaymentSystem.SOLIDGATE
              ? 'paymentmethod'
              : '';

      if (paymentSystem === PaymentSystem.RECURLY) {
        paymentRequest.on('error', handleError);
        paymentRequest.on('paymentAuthorized', handleAddress);
      }

      if (callbacks?.onMethod) {
        paymentRequest.on(event, callbacks?.onMethod);
      }

      return () => {
        if (paymentSystem === PaymentSystem.RECURLY) {
          paymentRequest.off('error', handleError);
          paymentRequest.off('paymentAuthorized', handleAddress);
        }

        if (callbacks?.onMethod) {
          paymentRequest.off(event, callbacks?.onMethod);
        }
      };
    }
  }, [paymentRequest, callbacks?.onMethod, paymentSystem, handleAddress]);

  return {
    canMakePayment,
    paymentRequest: canMakePayment ? paymentRequest : null,
  };
};

export default useApplePay;
